import { useCallback } from 'react';
import type { ParsedChapter } from '../../services/types';

interface ChapterPreviewTableProps {
  chapters: ParsedChapter[];
  onReorder: (chapters: ParsedChapter[]) => void;
  onTitleChange: (index: number, title: string) => void;
  onRemove: (index: number) => void;
}

export function ChapterPreviewTable({
  chapters,
  onReorder,
  onTitleChange,
  onRemove
}: ChapterPreviewTableProps) {
  const moveUp = useCallback((index: number) => {
    if (index <= 0) return;
    const newChapters = [...chapters];
    [newChapters[index - 1], newChapters[index]] = [newChapters[index], newChapters[index - 1]];
    // 更新 suggestedIndex
    newChapters.forEach((ch, i) => {
      ch.suggestedIndex = i + 1;
    });
    onReorder(newChapters);
  }, [chapters, onReorder]);

  const moveDown = useCallback((index: number) => {
    if (index >= chapters.length - 1) return;
    const newChapters = [...chapters];
    [newChapters[index], newChapters[index + 1]] = [newChapters[index + 1], newChapters[index]];
    // 更新 suggestedIndex
    newChapters.forEach((ch, i) => {
      ch.suggestedIndex = i + 1;
    });
    onReorder(newChapters);
  }, [chapters, onReorder]);

  const formatWordCount = (count: number) => {
    if (count >= 10000) {
      return `${(count / 10000).toFixed(1)} 萬`;
    }
    return count.toLocaleString();
  };

  if (chapters.length === 0) {
    return (
      <div className="text-center py-8 body-base">
        尚未上傳任何檔案
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-neutral-50 dark:bg-neutral-800/50">
          <tr>
            <th className="px-3 py-2 text-left text-sm font-medium text-neutral-500 dark:text-neutral-400 w-16">
              順序
            </th>
            <th className="px-3 py-2 text-left text-sm font-medium text-neutral-500 dark:text-neutral-400">
              標題
            </th>
            <th className="px-3 py-2 text-left text-sm font-medium text-neutral-500 dark:text-neutral-400 w-24">
              字數
            </th>
            <th className="px-3 py-2 text-left text-sm font-medium text-neutral-500 dark:text-neutral-400 w-40">
              原檔名
            </th>
            <th className="px-3 py-2 text-center text-sm font-medium text-neutral-500 dark:text-neutral-400 w-28">
              操作
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
          {chapters.map((chapter, index) => (
            <tr key={chapter.originalFilename} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/30">
              <td className="px-3 py-2 text-sm text-neutral-600 dark:text-neutral-400">
                {chapter.suggestedIndex}
              </td>
              <td className="px-3 py-2">
                <input
                  type="text"
                  value={chapter.suggestedTitle}
                  onChange={(e) => onTitleChange(index, e.target.value)}
                  className="input py-1 text-sm"
                />
              </td>
              <td className="px-3 py-2 text-sm text-neutral-600 dark:text-neutral-400">
                {formatWordCount(chapter.wordCount)}
              </td>
              <td className="px-3 py-2 text-sm text-neutral-500 dark:text-neutral-400 truncate max-w-[160px]" title={chapter.originalFilename}>
                {chapter.originalFilename.split('/').pop()}
              </td>
              <td className="px-3 py-2">
                <div className="flex items-center justify-center gap-1">
                  <button
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    className="p-1 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="上移"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => moveDown(index)}
                    disabled={index === chapters.length - 1}
                    className="p-1 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="下移"
                  >
                    ↓
                  </button>
                  <button
                    onClick={() => onRemove(index)}
                    className="p-1 text-danger-500 hover:text-danger-700 dark:text-danger-400 dark:hover:text-danger-300"
                    title="移除"
                  >
                    ✕
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
